import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { McIcon } from '@maersk-global/mds-react-wrapper/components-core';
import './Breadcrumb.css';

/** Map URL path segments to human-readable labels */
const SEGMENT_LABELS: Record<string, string> = {
    queries: 'Queries',
    create: 'Create Query',
    'smart-create': 'Smart Query Creator',
    schedules: 'Schedules',
    analytics: 'Analytics',
    regions: 'Regions',
    faq: 'Help Center',
    'developer-docs': 'API Reference',
    agents: 'AI Agents',
    edit: 'Edit Query',
    'db-breakglass': 'DB Breakglass',
};

/** Check if a segment looks like a dynamic ID (UUID or alphanumeric slug) */
const isDynamicSegment = (segment: string): boolean =>
    /^[0-9a-f-]{8,}$/i.test(segment) || /^[a-z0-9_-]{4,}$/i.test(segment);

/** Resolve a segment to a display label */
const getLabel = (segment: string, index: number, segments: string[]): string => {
    if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];

    // Dynamic segment inside /queries/:id → "Query Details"
    if (index > 0 && segments[index - 1] === 'queries' && isDynamicSegment(segment)) {
        return 'Query Details';
    }

    // Fallback: capitalize
    return segment
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
};

export const Breadcrumb: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const segments = location.pathname.split('/').filter(Boolean);

    // Don't render breadcrumb on home page
    if (segments.length === 0) return null;

    return (
        <nav className="breadcrumb-nav" aria-label="Breadcrumb">
            <div className="breadcrumb-item">
                <span className="breadcrumb-link home-link" onClick={() => navigate('/')}>
                    <McIcon icon="stack" style={{ fontSize: '14px' }} />
                    Home
                </span>
            </div>

            {segments.map((segment, index) => {
                const path = '/' + segments.slice(0, index + 1).join('/');
                const label = getLabel(segment, index, segments);
                const isLast = index === segments.length - 1;

                return (
                    <div className="breadcrumb-item" key={path}>
                        <span className="breadcrumb-separator">›</span>
                        {isLast ? (
                            <span className="breadcrumb-current">{label}</span>
                        ) : (
                            <span className="breadcrumb-link" onClick={() => navigate(path)}>
                                {label}
                            </span>
                        )}
                    </div>
                );
            })}
        </nav>
    );
};

export default Breadcrumb;
